import { useRouter } from 'next/router'
import React, { use, useEffect, useState } from 'react'
import { isPublicRoute } from '@/application/Auth/publicRoutes'
import { MONEYHUB_AUTH_CODE_FIELD, MONEYHUB_ERROR_FIELD } from '@/application/features/brokerConnection/fields'
import { DIRECT_OUTREACH_TOKEN_FIELD } from '@/application/features/directoutreach/fields'
import { UserContext } from '@/application/Providers/AuthProvider'
import { LoadingScreen } from '@/components/LoadingScreen'
import { getHashParam } from '@/utilities/hashParams'
import { useCookie, useSessionStorage } from '@/utilities/useBrowserStorage'

export const Page: React.FC = () => {
  const { asPath, replace, pathname } = useRouter()
  const { user } = use(UserContext)

  const [, setToken] = useCookie(DIRECT_OUTREACH_TOKEN_FIELD, '')
  const [, setCode] = useSessionStorage(MONEYHUB_AUTH_CODE_FIELD, '')
  const [, setError] = useSessionStorage(MONEYHUB_ERROR_FIELD, '')

  const [isLoading, setIsLoading] = useState(false)

  // Next produces a MPA
  // In order for Amplify to know the correct page to route you to, we need to setup
  // redirects via the Amplify console which is tricky to maintain
  // https://docs.amplify.aws/guides/hosting/nextjs/q/platform/js/#dynamic-routes
  //
  // The app is instead setup like a SPA, so the index page needs to figure out which
  // URL you wanted, then redirect you to that place. It's a bit of a hack but easier
  // than maintaining redirects in the Amplify console (for now)

  useEffect(() => {
    const handleRouting = async () => {
      // Direct outreach
      const isDirectOutreach = asPath.includes('direct-outreach')
      if (isDirectOutreach) {
        const tokenHash = getHashParam('token', asPath)
        if (tokenHash) {
          setToken(tokenHash)
        }
        const path = asPath.split('#')[0]
        replace(path)
        return
      }

      // Only show loading screen used to await auth info on protected routes
      if (!user.givenName && !isPublicRoute(asPath)) {
        setIsLoading(true)
        return
      }
      setIsLoading(false)
      if (asPath === '/' && pathname === '/') {
        if (user.returningUser === 'true') {
          replace('/my-account')
        } else {
          replace('/broker-selection')
        }
      } else {
        const tokenHash = getHashParam('token', asPath)
        const codeHash = getHashParam('code', asPath)
        const errorHash = getHashParam('error', asPath)

        // capture hash params in session storage as redirecting with the hash params causes
        // an infinite loop in nextjs
        if (tokenHash) {
          setToken(tokenHash)
        }
        if (codeHash) {
          setCode(codeHash)
        }
        if (errorHash) {
          setError(errorHash)
        }

        const path = asPath.split('#')[0]
        replace(path)
      }
    }
    handleRouting()
    // removed replace as a dependency as it was causing infinite rerender in
    // conjunction with MSAL redirect when code appended to the URL
  }, [asPath, pathname, user, setToken, setCode, setError])

  if (isLoading) {
    return <LoadingScreen />
  }

  return <></>
}

export default Page
